import React, { useLayoutEffect, useRef, useState } from "react";
import { pdfjs, Document, Page, DocumentProps } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "core-js/proposals/promise-with-resolvers";
import AskPassword from "./AskPassword";
import DocumentLocked from "../DocumentLocked";
import useTranslation from "../../../../hooks/useTranslation";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

// Emitted by Rsbuild next to the bundles (output.copy in rsbuild.config.ts);
// ASSET_PREFIX is injected by Rsbuild ("/packs/" in production, the dev-server
// origin in development) and ends with a slash — normalized here to be safe.
const assetPrefix = (process.env.ASSET_PREFIX ?? "").replace(/\/$/, "");

const pdfOptions = {
    cMapUrl: `${assetPrefix}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${assetPrefix}/standard_fonts/`,
};

const HORIZONTAL_PADDING = 64;

type ReactPdfOnPassword = NonNullable<DocumentProps["onPassword"]>;

export interface PasswordPrompt {
    attempt: number;
    status: number;
    verifyPassword: (password: string) => void;
}

interface Props {
    file: string | null;
    scale: number;
    rotation: number;
    numPages: number;
    passwordPrompt: PasswordPrompt | null;
    onLoadSuccess: (pdf: { numPages: number }) => void;
    onLoadProgress: (info: { loaded: number; total: number }) => void;
    onLoadError: (error: Error) => void;
    onPassword: (callback: (password: string | Error) => void, reason: number) => void;
}

export default function PDFViewer({
    file,
    scale,
    rotation,
    numPages,
    passwordPrompt,
    onLoadSuccess,
    onLoadProgress,
    onLoadError,
    onPassword,
}: Props) {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [dismissedAttempt, setDismissedAttempt] = useState<number | null>(null);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const update = () => setContainerWidth(el.clientWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const dismissed = !!passwordPrompt && dismissedAttempt === passwordPrompt.attempt;

    const pageWidth =
        containerWidth > 0 ? Math.max(0, containerWidth - HORIZONTAL_PADDING) * scale : undefined;

    return (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-2">
            {passwordPrompt &&
                (dismissed ? (
                    <DocumentLocked onUnlock={() => setDismissedAttempt(null)} />
                ) : (
                    <AskPassword
                        key={passwordPrompt.attempt}
                        status={passwordPrompt.status}
                        verifyPassword={passwordPrompt.verifyPassword}
                        onCancel={() => setDismissedAttempt(passwordPrompt.attempt)}
                    />
                ))}
            <div
                ref={containerRef}
                inert={!!passwordPrompt}
                className="flex-1 overflow-auto px-8 py-8"
            >
                {pageWidth !== undefined && (
                    <Document
                        file={file}
                        rotate={rotation}
                        onLoadSuccess={onLoadSuccess}
                        onLoadProgress={onLoadProgress}
                        onLoadError={onLoadError}
                        onPassword={onPassword as ReactPdfOnPassword}
                        options={pdfOptions}
                        error={
                            <div className="p-8 text-center text-sm text-danger">
                                {t("pdf_viewer.document.load_error")}
                            </div>
                        }
                        loading={null}
                        className="mx-auto flex flex-col items-safe-center gap-6"
                    >
                        {Array.from(new Array(numPages), (_, i) => (
                            <div
                                key={`page_${i + 1}`}
                                style={{ width: pageWidth }}
                                className="overflow-hidden rounded-sm bg-white shadow-[0_8px_24px_0_rgb(0_0_0/0.08)]"
                            >
                                <Page
                                    pageIndex={i}
                                    width={pageWidth}
                                    error={t("pdf_viewer.page.load_error")}
                                    loading={t("pdf_viewer.page.loading")}
                                />
                            </div>
                        ))}
                    </Document>
                )}
            </div>
        </div>
    );
}
